(() => {
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'EUR'
  }).format((Number(cents) || 0) / 100);

  const initReveal = (root = document) => {
    const items = root.querySelectorAll('.hh-reveal:not([data-hh-ready])');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('hh-is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('hh-is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: .12 });
    items.forEach((item) => { item.dataset.hhReady = 'true'; observer.observe(item); });
  };

  const initSliders = (root = document) => {
    root.querySelectorAll('[data-hh-slider]:not([data-hh-ready])').forEach((slider) => {
      slider.dataset.hhReady = 'true';
      const track = slider.querySelector('[data-hh-track]');
      if (!track) return;
      slider.querySelector('[data-hh-prev]')?.addEventListener('click', () => {
        track.scrollBy({ left: -Math.max(track.clientWidth * .82, 280), behavior: 'smooth' });
      });
      slider.querySelector('[data-hh-next]')?.addEventListener('click', () => {
        track.scrollBy({ left: Math.max(track.clientWidth * .82, 280), behavior: 'smooth' });
      });
    });
  };

  const initBeforeAfter = (root = document) => {
    root.querySelectorAll('[data-hh-before-after]:not([data-hh-ready])').forEach((widget) => {
      widget.dataset.hhReady = 'true';
      widget.querySelector('input')?.addEventListener('input', (event) => {
        widget.style.setProperty('--hh-position', `${event.target.value}%`);
      });
    });
  };

  const initTabs = (root = document) => {
    root.querySelectorAll('[data-hh-tabs]:not([data-hh-ready])').forEach((tabs) => {
      tabs.dataset.hhReady = 'true';
      const buttons = [...tabs.querySelectorAll('[role="tab"]')];
      const panels = [...tabs.querySelectorAll('[role="tabpanel"]')];
      buttons.forEach((button, index) => button.addEventListener('click', () => {
        buttons.forEach((item) => item.setAttribute('aria-selected', 'false'));
        panels.forEach((panel) => { panel.hidden = true; });
        button.setAttribute('aria-selected', 'true');
        if (panels[index]) panels[index].hidden = false;
      }));
    });
  };

  const initCountdowns = (root = document) => {
    root.querySelectorAll('[data-hh-countdown]:not([data-hh-ready])').forEach((clock) => {
      clock.dataset.hhReady = 'true';
      let target = Date.parse(clock.dataset.hhCountdown || '');
      if (!Number.isFinite(target) || target <= Date.now()) {
        target = Date.now() + (Number(clock.dataset.hhFallbackDays || 7) * 86400000);
      }
      const tick = () => {
        const distance = Math.max(0, target - Date.now());
        const values = {
          days: Math.floor(distance / 86400000),
          hours: Math.floor((distance / 3600000) % 24),
          minutes: Math.floor((distance / 60000) % 60),
          seconds: Math.floor((distance / 1000) % 60)
        };
        Object.entries(values).forEach(([key, value]) => {
          const node = clock.querySelector(`[data-hh-${key}]`);
          if (node) node.textContent = String(value).padStart(2, '0');
        });
      };
      tick();
      const timer = window.setInterval(tick, 1000);
      clock.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => clearInterval(timer), { once: true });
    });
  };

  const initVariantForms = (root = document) => {
    root.querySelectorAll('[data-hh-product-form]:not([data-hh-ready])').forEach((form) => {
      form.dataset.hhReady = 'true';
      const select = form.querySelector('[data-hh-variant-select]');
      const id = form.querySelector('input[name="id"]');
      if (select && id) select.addEventListener('change', () => { id.value = select.value; });
    });
  };

  const initBundles = (root = document) => {
    root.querySelectorAll('[data-hh-bundle-form]:not([data-hh-ready])').forEach((form) => {
      form.dataset.hhReady = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const status = form.querySelector('[data-hh-bundle-status]');
        const items = [...form.querySelectorAll('input[name="items"]:checked')].map((input) => ({ id: Number(input.value), quantity: 1 }));
        if (!items.length) { if (status) status.textContent = 'Sélectionnez au moins un produit.'; return; }
        if (button) button.disabled = true;
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items })
          });
          if (!response.ok) throw new Error('Cart error');
          if (status) status.textContent = 'La sélection a été ajoutée au panier.';
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (_) {
          if (status) status.textContent = 'Impossible d’ajouter cette sélection. Vérifiez la disponibilité.';
        } finally { if (button) button.disabled = false; }
      });
    });
  };

  const renderStoredProducts = async (container, handles) => {
    const unique = [...new Set(handles)].filter(Boolean).slice(0, Number(container.dataset.hhLimit || 8));
    if (!unique.length) {
      container.innerHTML = `<p class="hh-status">${container.dataset.hhEmpty || 'Aucun produit pour le moment.'}</p>`;
      return;
    }
    const products = await Promise.all(unique.map(async (handle) => {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        return response.ok ? response.json() : null;
      } catch (_) { return null; }
    }));
    container.innerHTML = products.filter(Boolean).map((product) => `
      <article class="hh-card hh-product-card">
        <a class="hh-product-card__media" href="${product.url}"><img src="${product.featured_image || ''}" alt="${product.title}" loading="lazy"></a>
        <div class="hh-product-card__body"><h3><a class="hh-product-card__title" href="${product.url}">${product.title}</a></h3><div class="hh-price"><strong>${money(product.price)}</strong></div></div>
      </article>`).join('');
  };

  const initStoredProducts = (root = document) => {
    const currentNode = document.getElementById('HHCurrentProduct');
    if (currentNode && !currentNode.dataset.hhStored) {
      currentNode.dataset.hhStored = 'true';
      try {
        const current = JSON.parse(currentNode.textContent);
        const recent = JSON.parse(localStorage.getItem('hhRecentlyViewed') || '[]').filter((handle) => handle !== current.handle);
        localStorage.setItem('hhRecentlyViewed', JSON.stringify([current.handle, ...recent].slice(0, 12)));
      } catch (_) {}
    }
    root.querySelectorAll('[data-hh-stored-products]:not([data-hh-ready])').forEach((container) => {
      container.dataset.hhReady = 'true';
      const key = container.dataset.hhStoredProducts === 'wishlist' ? 'hhWishlist' : 'hhRecentlyViewed';
      try { renderStoredProducts(container, JSON.parse(localStorage.getItem(key) || '[]')); } catch (_) { renderStoredProducts(container, []); }
    });
    root.querySelectorAll('[data-hh-wishlist]:not([data-hh-ready])').forEach((button) => {
      button.dataset.hhReady = 'true';
      const handle = button.dataset.hhWishlist;
      const sync = () => {
        const list = JSON.parse(localStorage.getItem('hhWishlist') || '[]');
        button.setAttribute('aria-pressed', String(list.includes(handle)));
        button.textContent = list.includes(handle) ? '♥ Retirer des favoris' : '♡ Ajouter aux favoris';
      };
      button.addEventListener('click', () => {
        const list = JSON.parse(localStorage.getItem('hhWishlist') || '[]');
        localStorage.setItem('hhWishlist', JSON.stringify(list.includes(handle) ? list.filter((item) => item !== handle) : [handle, ...list]));
        sync();
      });
      sync();
    });
  };

  const initTracking = (root = document) => {
    root.querySelectorAll('[data-hh-tracking-form]:not([data-hh-ready])').forEach((form) => {
      form.dataset.hhReady = 'true';
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const value = form.querySelector('input')?.value.trim();
        const message = form.querySelector('[data-hh-tracking-message]');
        if (!value) return;
        if (/^https?:\/\//i.test(value)) window.location.assign(value);
        else if (message) message.textContent = 'Collez le lien de suivi reçu dans votre e-mail de confirmation.';
      });
    });
  };

  const initColorChangers = (root = document) => {
    root.querySelectorAll('[data-hh-color-changer]:not([data-hh-ready])').forEach((section) => {
      section.dataset.hhReady = 'true';
      const scope = section.dataset.hhScope === 'page' ? document.querySelector('.page-wrapper') : section;
      if (!scope) return;
      if (section.dataset.hhPageBg) scope.style.backgroundColor = section.dataset.hhPageBg;
      if (section.dataset.hhPageText) scope.style.color = section.dataset.hhPageText;
    });
  };

  const init = (root = document) => {
    initReveal(root); initSliders(root); initBeforeAfter(root); initTabs(root);
    initCountdowns(root); initVariantForms(root); initBundles(root); initStoredProducts(root);
    initTracking(root); initColorChangers(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
